import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAdminProducts, setIsNeedRefresh } from "../redux/adminShopSlice";
import PageContainer from "../components/PageContainer";
import PageTitle from "../components/PageTitle";
import Loader from "../components/Loader";
import CustomHr from "../components/CustomHr";
import QuillTextarea from "../components/QuillTextarea";
import UploadPhotos from "../components/UploadPhotos";
import { GreenButton } from "../components/buttons";
import { setBacklink } from "../redux/backlinkSlice";

const AddEditProduct = () => {
    // Check if add or edit
    const [searchParams] = useSearchParams();
    const productId = searchParams.get('productId');
    const [isEditMode, setIsEditMode] = useState(false);
    const [productData, setProductData] = useState(null);
    const { data, isLoading } = useSelector(state => state.adminShop);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setBacklink("/handle-shop")) // Imposto il backlink su ShoHandleShopp
    }, [])

    // Recupero i dati in modalità edit (da tutti i prodotti in Redux, non rifaccio una chiamata singola), anche in caso si refreshi in modalità edit
    useEffect(() => {
        if (!data && productId) {
            dispatch(getAdminProducts());
        } else if (data && productId) {
            setIsEditMode(true);
            setProductData(data.find(element => element._id === productId));
        }
    }, [productId, data])

    // Imposto inputData coi dati del prodotto trovato
    useEffect(() => {
        if (productData) {
            setInputData(productData)
        }
    }, [productData])

    // Handle inputData
    const [inputData, setInputData] = useState({
        isActive: false,
        title: "",
        description: "",
        price: "",
        gallery: [],
        coverImageUrl: "",
        size: [],
        packageWeight: "",
        packageLength: "",
        packageWidth: "",
        packageHeight: "",
    })
    const handleInputData = (event) => {
        const { id, value } = event.target;
        let fixedId = id;
        let fixedValue = value;
        if (id === 'isActive') {
            if (value === 'Pubblico') fixedValue = true;
            if (value === 'Bozza') fixedValue = false;
        }
        if (id === "packageWeight" || id === "packageLength" || id === "packageWidth" || id === "packageHeight") {
            fixedValue = Number(fixedValue)
        }
        setInputData(prevState => ({
            ...prevState,
            [fixedId]: fixedValue
        }))
    }

    // Handle select cover image
    const handleSelectCover = (index) => {
        let newCoverImage = { ...inputData.gallery[index] };
        if (inputData.coverImageUrl) {
            if (newCoverImage.public_id === inputData.coverImageUrl.public_id) {
                setInputData(prevState => ({
                    ...prevState,
                    coverImageUrl: null
                }))
            } else {
                setInputData(prevState => ({
                    ...prevState,
                    coverImageUrl: newCoverImage
                }))
            }
        } else {
            setInputData(prevState => ({
                ...prevState,
                coverImageUrl: newCoverImage
            }))
        }
    }

    // Gestisco UX campi obbligatori
    const [isFieldsError, setIsFieldsError] = useState(false);
    const [fieldsChecked, setFieldsChecked] = useState(false);
    useEffect(() => {
        let requiredFields = {};
        inputData.title ? requiredFields.title = true : requiredFields.title = false;
        inputData.description ? requiredFields.description = true : requiredFields.description = false;
        inputData.price ? requiredFields.price = true : requiredFields.price = false;
        inputData.packageWeight ? requiredFields.packageWeight = true : requiredFields.packageWeight = false;
        inputData.packageLength ? requiredFields.packageLength = true : requiredFields.packageLength = false;
        inputData.packageWidth ? requiredFields.packageWidth = true : requiredFields.packageWidth = false;
        inputData.packageHeight ? requiredFields.packageHeight = true : requiredFields.packageHeight = false;
        let allFieldsAreTrue = Object.values(requiredFields).every(Boolean);
        allFieldsAreTrue ? setFieldsChecked(true) : setFieldsChecked(false);
    }, [inputData]);
    const handleCheckFieldsAndSendData = () => {
        fieldsChecked ? sendData() : setIsFieldsError(true);
    };
    useEffect(() => {
        setTimeout(() => {
            setIsFieldsError(false);
        }, 3000);
    }, [isFieldsError])

    // Send data fetch
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [fetchError, setFetchError] = useState(null);
    const navigate = useNavigate();
    const sendData = async () => {
        setFetchStatus('loading')
        const token = localStorage.getItem('ticino-wild-token');
        const url = isEditMode ? `${process.env.REACT_APP_SERVER_BASE_URL}/products/edit-product` : `${process.env.REACT_APP_SERVER_BASE_URL}/products/add-new-product`;
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
        const options = {
            method: isEditMode ? 'PATCH' : 'POST',
            headers,
            body: JSON.stringify(inputData)
        }
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                setFetchStatus('succeeded');
                setTimeout(() => {
                    /* dispatch(setIsNeedHomeRefresh(true)); */ // Sostituisci questo con un refresh dello shop per lo user
                    dispatch(setIsNeedRefresh(true));
                    navigate("/handle-shop");
                }, 2000)
            } else {
                const error = await response.json();
                setFetchError(error);
                setFetchStatus('failed');
            }
        } catch (error) {
            setFetchError(error);
            setFetchStatus('failed');
        }
    }

    // Gestisco l'inserimento di nuove taglie
    const [sizeInput, setSizeInput] = useState("");
    const handleSizeInput = (event) => {
        const { value } = event.target;
        setSizeInput(value);
    }
    const setToInputData = () => {
        if (!inputData.size.includes(sizeInput) && sizeInput) {
            setInputData(prevState => ({
                ...prevState,
                size: [...prevState.size, sizeInput]
            }));
        }
        setSizeInput('');
    }
    
    // Gestisco l'eliminazione di una taglia
    const deleteFromInputData = (event) => {
        const { id } = event.target;
        const elementToDelete = inputData.size.slice(id, id + 1); // Slice restituisce un array di un elemento
        const newArray = inputData.size.filter(element => element !== elementToDelete[0]);
        setInputData(prevState => ({
            ...prevState,
            size: newArray
        }))
    }

    return (
        <PageContainer px="4" desktopItems="center" isRelative>
            <PageTitle title={isEditMode ? 'Modifica prodotto' : 'Aggiungi prodotto'} backLink="/handle-shop" />

            {/*  Loader con overlay nel caso in modalità edit si refreshi */}
            {
                isLoading &&
                <div className="absolute inset-0 z-10 bg-blue-400 bg-opacity-90"> {/* bg-[#f7f7f8] */}
                    <div className="w-full flex justify-center mt-24">
                        <Loader />
                    </div>
                </div>
            }

            {/* Informazioni prodotto */}
            <h2 className="mt-8">Informazioni prodotto</h2>
            <CustomHr />

            {/* Title */}
            <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">
                <label htmlFor="title">Titolo <span className="text-xl">*</span></label>
                <input type="text" id="title" placeholder="Nome del prodotto" className="lg:w-[800px]" onChange={handleInputData} value={inputData.title ? inputData.title : null} />
            </div>

            {/* Description */}
            <div className="flex flex-col p-2 gap-2 w-full lg:w-[800px]">
                <label htmlFor="description">Descrizione <span className="text-xl">*</span></label>
                <QuillTextarea setInputData={setInputData} initContent={inputData.description} />
            </div>

            {/* Photos */}
            <UploadPhotos inputDataGallery={inputData.gallery} setInputData={setInputData} coverPublicId={inputData.coverImageUrl ? inputData.coverImageUrl.public_id : null} handleSelectCover={handleSelectCover} type="Product" />

            {/* Prezzo */}
            <h2 className="mt-8">Prezzo</h2>
            <CustomHr />
            <div className="flex items-center p-2 gap-2 w-full lg:w-[800px] mt-8">
                <label htmlFor="price">Prezzo </label>
                <input type="number" min="0" id="price" className="w-24" onChange={handleInputData} value={inputData.price} />
            </div>

            {/* Dimensioni e peso */}
            <h2 className="mt-8">Dimensioni e peso</h2>
            <CustomHr />
            <div className="flex flex-col items-start p-2 gap-2 w-full lg:w-[800px] mt-8">
                <h4 className="text-neutral-500 italic mb-4">Per il calcolo delle spese di spedizione in fase di acquisto (in relazione allo zip code del destinatario)</h4>
                <div className="flex flex-col lg:flex-row gap-2">
                    {/* Lunghezza */}
                    <div className="flex gap-2 items-center">
                        <label htmlFor="packageLength" className="text-sm">Lunghezza (cm)</label>
                        <input type="number" min="0" id="packageLength" className="w-48" onChange={handleInputData} value={inputData.packageLength === 0 ? "" : inputData.packageLength} />
                    </div>
                    {/* Larghezza */}
                    <div className="flex gap-2 items-center">
                        <label htmlFor="packageWidth" className="text-sm">Larghezza (cm)</label>
                        <input type="number" min="0" id="packageWidth" className="w-48" onChange={handleInputData} value={inputData.packageWidth === 0 ? "" : inputData.packageWidth} />
                    </div>
                    {/* Altezza */}
                    <div className="flex gap-2 items-center">
                        <label htmlFor="packageHeight" className="text-sm">Altezza (cm)</label>
                        <input type="number" min="0" id="packageHeight" className="w-48" onChange={handleInputData} value={inputData.packageHeight === 0 ? "" : inputData.packageHeight} />
                    </div>
                </div>
                {/* Peso */}
                <div className="flex gap-2 items-center">
                    <label htmlFor="packageWeight" className="text-sm">Peso (Kg)</label>
                    <input type="number" min="0" id="packageWeight" className="w-48" onChange={handleInputData} value={inputData.packageWeight === 0 ? "" : inputData.packageWeight} />
                </div>


            </div>

            {/* SIZE */}
            <h2 className="mt-8">Taglie disponibili</h2>
            <CustomHr />
            <div>
                <label htmlFor="size">Inserisci nuova taglia disponibile</label>
                <h6 className="italic text-xs">(Puoi non inserire taglie per questo prodotto.)</h6>
                <div className="flex gap-2 max-w-[800px] lg:w-[800px]">
                    <input type="text" className="flex-grow" onChange={handleSizeInput} value={sizeInput} />
                    {sizeInput && <button className="w-[72px] h-[72px] rounded-lg border-2 border-[#225424] text-[#225424] flex justify-center items-center text-4xl pb-1" onClick={setToInputData}>+</button>}
                    {!sizeInput && <button className="w-[72px] h-[72px] rounded-lg border-2 border-neutral-300 text-neutral-300 flex justify-center items-center text-4xl pb-1 cursor-not-allowed">+</button>}
                </div>
            </div>
            <div className="flex flex-col gap-2 mt-8">
                {
                    inputData && inputData.size && inputData.size.length > 0 && inputData.size.map((element, index) => {
                        return <div key={index} className='bg-white pl-4 pr-2 rounded-full border-2 cursor-pointer flex justify-between items-center gap-2' ><div>{element}</div><div className="pt-2 cursor-pointer"><i className="fi fi-rr-circle-xmark text-3xl cursor-pointer" id={index} onClick={deleteFromInputData}></i></div></div>
                    })
                }
            </div>



            {/* Bozza o pubblico */}
            <div className="flex flex-col gap-2 mt-8">
                <label htmlFor="isActive">Seleziona la modalità di salvataggio</label>
                <select id="isActive" className="w-48" onChange={handleInputData} value={inputData.isActive ? 'Pubblico' : 'Bozza'}>
                    <option value="Bozza">Bozza</option>
                    <option value="Pubblico">Pubblico</option>
                </select>
                <div className="w-full lg:w-[800px]">
                    <p>Tenendo la modalità 'Bozza' il prodotto verrà salvato e potrete visualizzarlo nel vostro elenco di gestione prodotti, ma esso non sarà visibile agli utenti.</p>
                </div>
            </div>

            <div className="mt-8">
                {fetchStatus === 'idle' && isFieldsError && <h4 className="text-red-500 mb-4">Compila tutti i campi obbligatori</h4>}
                {fetchStatus === 'idle' && !isFieldsError && <div className="w-full"><GreenButton text={isEditMode ? 'Salva modifiche' : 'Salva prodotto'} isMobileFull click={handleCheckFieldsAndSendData} /></div>}
                {fetchStatus === 'loading' && <Loader />}
                {fetchStatus === 'succeeded' && !isEditMode && <h3>Nuovo prodotto inserito correttamente, attendi il refresh della pagina</h3>}
                {fetchStatus === 'succeeded' && isEditMode && <h3>Prodotto modificato correttamente, attendi il refresh della pagina</h3>}
                {fetchStatus === 'failed' && <h3>Qualcosa è andato storto, ricarica la pagina e riprova.</h3>}
            </div>

        </PageContainer >
    )
}

export default AddEditProduct;